import React, { useState, useEffect, useRef } from 'react';
import { Leaf, X, Send, Loader2, Plus } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

/**
 * Vine Voice — Pastor Obed's AI assistant.
 *
 * Floating bubble bottom-right. Click to expand a chat panel.
 * Persists session id in localStorage so the conversation survives navigation.
 * Calls POST /api/vine/chat — backend decides mentee vs admin mode by role.
 */
const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'vine_voice_session_id';

/**
 * Lightweight inline markdown renderer for Vine Voice replies.
 * Supports: **bold**, [label](url) links (internal → react-router Link,
 * external → <a target=_blank>), and preserves newlines.
 * No dangerouslySetInnerHTML — safe by construction.
 */
const renderInline = (text) => {
  // Tokenise on links first so bold inside link labels still works.
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  const out = [];
  let last = 0;
  let m;
  let key = 0;
  while ((m = linkRe.exec(text)) !== null) {
    if (m.index > last) out.push(...renderBold(text.slice(last, m.index), key++));
    const [, label, href] = m;
    const isInternal = href.startsWith('/');
    if (isInternal) {
      out.push(
        <Link
          key={`l-${key++}`}
          to={href}
          className="inline-flex items-center gap-1 px-2 py-0.5 mt-0.5 rounded-full bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200 text-xs font-medium transition-colors"
          data-testid="vine-recommendation-link"
        >
          {label}
        </Link>
      );
    } else {
      out.push(
        <a
          key={`a-${key++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-emerald-700 underline hover:text-emerald-900"
        >
          {label}
        </a>
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(...renderBold(text.slice(last), key++));
  return out;
};

const renderBold = (text, baseKey) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return (
        <strong key={`b-${baseKey}-${i}`} className="font-semibold">
          {p.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={`t-${baseKey}-${i}`}>{p}</React.Fragment>;
  });
};

const VineMessageBody = ({ text }) => {
  if (!text) return null;
  const lines = text.split('\n');
  return (
    <>
      {lines.map((line, i) => (
        <React.Fragment key={i}>
          {renderInline(line)}
          {i < lines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </>
  );
};

const VineVoiceBubble = ({ pageContext }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || '');
  const [error, setError] = useState('');
  const scrollRef = useRef(null);

  // Hide on community page (the bubble overlaps the send composer)
  const hiddenPaths = ['/community'];
  const isHidden = hiddenPaths.some((p) => location.pathname.startsWith(p));

  // Auto-scroll on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Rehydrate conversation on open
  useEffect(() => {
    if (!open || !sessionId || messages.length > 0) return;
    const token = localStorage.getItem('access_token');
    if (!token) return;
    fetch(`${API}/api/vine/history?session_id=${encodeURIComponent(sessionId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : { turns: [] }))
      .then((d) => {
        if (Array.isArray(d.turns) && d.turns.length) {
          setMessages(d.turns.map((t, idx) => ({ id: `h-${idx}-${t.at || ''}`, role: t.role, text: t.text })));
        }
      })
      .catch(() => {});
  }, [open, sessionId, messages.length]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setError('');
    setInput('');
    const userMsgId = `u-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setMessages((m) => [...m, { id: userMsgId, role: 'user', text }]);
    setSending(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API}/api/vine/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: text, session_id: sessionId || undefined, page_context: pageContext }),
      });
      const data = await res.json().catch(() => ({}));
      const assistantId = `a-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      if (!res.ok) {
        setError(data.detail || `Error ${res.status}`);
        setMessages((m) => [...m, { id: assistantId, role: 'assistant', text: `Sorry — ${data.detail || 'something went wrong'}.` }]);
      } else {
        if (data.session_id && data.session_id !== sessionId) {
          setSessionId(data.session_id);
          localStorage.setItem(SESSION_KEY, data.session_id);
        }
        setMessages((m) => [...m, { id: assistantId, role: 'assistant', text: data.reply }]);
      }
    } catch (e) {
      setError(String(e));
      setMessages((m) => [...m, { id: `a-${Date.now()}`, role: 'assistant', text: 'Sorry — connection issue. Try again in a moment.' }]);
    } finally {
      setSending(false);
    }
  };

  // Don't render at all if user is not logged in or on hidden pages
  if (isHidden) return null;
  if (typeof window !== 'undefined' && !localStorage.getItem('access_token')) return null;

  return (
    <>
      {/* Floating Bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="vine-voice-bubble"
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-2xl hover:scale-105 transition-transform flex items-center justify-center group"
          title="Vine Voice — Pastor Obed's assistant"
        >
          <Leaf className="w-6 h-6 group-hover:rotate-12 transition-transform" />
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-400 animate-pulse" />
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-2rem)] flex flex-col bg-white rounded-2xl shadow-2xl border border-emerald-200 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-emerald-700 via-emerald-600 to-emerald-500 text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Leaf className="w-4 h-4" />
              </div>
              <div>
                <div className="font-semibold text-sm">Vine Voice</div>
                <div className="text-emerald-50 text-[11px] opacity-90">Pastor Obed&apos;s assistant</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  setMessages([]);
                  setSessionId('');
                  setError('');
                  localStorage.removeItem(SESSION_KEY);
                }}
                data-testid="vine-voice-new-chat"
                title="Start a new conversation"
                className="p-1.5 hover:bg-white/15 rounded transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
              <button onClick={() => setOpen(false)} data-testid="vine-voice-close" className="p-1.5 hover:bg-white/15 rounded transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FDF6E3] bg-opacity-50">
            {messages.length === 0 && (
              <div className="text-center py-10 text-emerald-800/70">
                <Leaf className="w-8 h-8 mx-auto mb-3 text-emerald-600" />
                <p className="text-sm font-medium text-emerald-900 mb-1">Shalom — I&apos;m Vine Voice.</p>
                <p className="text-xs leading-relaxed">
                  I&apos;m Pastor Obed&apos;s assistant. Ask me to explain a verse, help you through a lesson,
                  or pray with you. I&apos;m here in confidence.
                </p>
                <div className="mt-4 flex flex-col gap-1.5 text-xs">
                  {['Explain shalom in Hebrew', 'I&apos;m stuck on Lesson 2', 'Pray with me about my day'].map((s) => (
                    <button
                      key={s}
                      onClick={() => setInput(s.replace('&apos;', "'"))}
                      className="px-3 py-1.5 bg-white border border-emerald-200 text-emerald-800 rounded-full hover:bg-emerald-50 transition-colors text-left"
                      dangerouslySetInnerHTML={{ __html: '› ' + s }}
                    />
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={m.id || `m-${i}`} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-emerald-600 text-white rounded-br-sm whitespace-pre-wrap'
                      : 'bg-white text-gray-800 rounded-bl-sm border border-emerald-100'
                  }`}
                >
                  {m.role === 'assistant' ? <VineMessageBody text={m.text} /> : m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-500 rounded-2xl rounded-bl-sm border border-emerald-100 px-3 py-2 text-sm flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Vine Voice is reflecting…
                </div>
              </div>
            )}
            {error && (
              <p className="text-xs text-red-600 text-center">{error}</p>
            )}
          </div>

          {/* Input */}
          <div className="px-3 py-3 border-t border-emerald-100 bg-white flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
              placeholder="Ask Vine Voice…"
              disabled={sending}
              data-testid="vine-voice-input"
              className="flex-1 px-3 py-2 text-sm rounded-full border border-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:opacity-50"
            />
            <button
              onClick={send}
              disabled={sending || !input.trim()}
              data-testid="vine-voice-send"
              className="w-9 h-9 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center disabled:opacity-50 transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default VineVoiceBubble;
